import React from 'react';

import { Modal } from 'react-bootstrap';
import { AppSyncItem } from '../../models';
import { AppSyncItemStatus } from '../../models-enums';
import { ProgressBar } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import * as types from '../../actions/actionTypes';
import { userLogoutSessionOnly } from '../../actions/userActions';
import { useTranslation } from 'react-i18next';

import logo from '../../images/LoadingLogo.png';

interface Props {
    show: boolean;
    message?: string;
    syncItems?: AppSyncItem[];
    downloadSpeed: number | undefined;
    restartAppSync?: () => void;
    productProcessingProgress?: {
        page: number;
        currentBatch: number;
        totalBatches: number;
        productsProcessed: number;
        totalProducts: number;
    } | null;
}

interface SyncProgress {
    currentIndex: number;
    totalItems: number;
}
const Loading: React.FC<Props> = props => {
    const [displaySyncItems, setDisplaySyncItems] = React.useState<AppSyncItem[]>([]);
    const [syncProgress, setSyncProgress] = React.useState<SyncProgress>({ currentIndex: 0, totalItems: 0 });
    const [elapsedTime, setElapsedTime] = React.useState<number>(0);
    const [startTime, setStartTime] = React.useState<number | null>(null);
    const [hasRecordCounts, setHasRecordCounts] = React.useState<boolean>(false);
    const [maxProgressReached, setMaxProgressReached] = React.useState<number>(0);
    const [lastTotalRecords, setLastTotalRecords] = React.useState<number>(0);
    const [lastProgress, setLastProgress] = React.useState<number>(0);
    const [shouldAnimateProgress, setShouldAnimateProgress] = React.useState<boolean>(true);

    const dispatch = useDispatch();
    const { t } = useTranslation();

    // Timer effect
    React.useEffect(() => {
        if (props.show) {
            // Start timer when dialog opens
            if (startTime === null) {
                setStartTime(Date.now());
                setElapsedTime(0);
            }

            const interval = setInterval(() => {
                if (startTime !== null) {
                    const elapsed = Math.floor((Date.now() - startTime) / 1000);
                    setElapsedTime(elapsed);
                }
            }, 1000);

            return () => clearInterval(interval);
        } else {
            // Reset timer when dialog closes
            setStartTime(null);
            setElapsedTime(0);
        }
    }, [props.show, startTime]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => {
        // Only run this logic if the modal is showing
        if (props.show) {
            // First Load
            if (displaySyncItems.length === 0 && props.syncItems) {
                setDisplaySyncItems(props.syncItems);
                setSyncProgress({ currentIndex: 0, totalItems: props.syncItems.length });
                setHasRecordCounts(false);
                setMaxProgressReached(0);
                setLastTotalRecords(0);
                setLastProgress(0);
                setShouldAnimateProgress(true);
            } else if (
                props.syncItems &&
                displaySyncItems.length === 1 &&
                displaySyncItems[0]?.name === '' &&
                displaySyncItems[0]?.displayName === ''
            ) {
                // If something fails and we're retrying, displaySyncItems will have a single item with empty strings for some reason
                setDisplaySyncItems(props.syncItems);
                setSyncProgress({ currentIndex: 0, totalItems: props.syncItems.length });
                setHasRecordCounts(false);
                setMaxProgressReached(0);
                setLastTotalRecords(0);
                setLastProgress(0);
                setShouldAnimateProgress(true);
            } else if (displaySyncItems.length > 0 && props.syncItems && props.syncItems.length > 0) {
                // Props.SyncItems array will shrink as API calls are completed, so if we notice a difference, we can assume that an item has been completed
                let itemsCompleted = 0;
                let foundRecordCounts = false;

                displaySyncItems.forEach((item: AppSyncItem) => {
                    const foundItem = props.syncItems?.find((syncItem: AppSyncItem) => syncItem.name === item.name);

                    if (!foundItem) {
                        itemsCompleted++;
                        item.status = AppSyncItemStatus.success;
                    } else {
                        item.status = foundItem.status;
                        // Update record count and processed info from current sync items
                        if (foundItem.recordCount !== undefined) {
                            item.recordCount = foundItem.recordCount;
                            foundRecordCounts = true;
                        }
                        if (foundItem.recordsProcessed !== undefined) {
                            item.recordsProcessed = foundItem.recordsProcessed;
                        }
                    }
                });

                // Track if we've ever seen record counts
                if (foundRecordCounts || props.syncItems.some(item => (item.recordCount || 0) > 1)) {
                    setHasRecordCounts(true);
                }

                setDisplaySyncItems([...displaySyncItems]);
                setSyncProgress({ ...syncProgress, currentIndex: itemsCompleted });
            }
        } else {
            // If we're not showing the modal, make sure the local state is cleared
            if (displaySyncItems.length > 0) {
                setDisplaySyncItems([]);
                setHasRecordCounts(false);
                setMaxProgressReached(0);
                setLastTotalRecords(0);
                setLastProgress(0);
                setShouldAnimateProgress(true);
            }

            // If we're not showing the modal, make sure the download speed is cleared for next use
            if (props.downloadSpeed) {
                dispatch({ type: types.SET_DOWNLOAD_SPEED, payload: undefined });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.syncItems, props.show]);

    // State to track the current display text and prevent changes during total updates
    const [currentDisplayText, setCurrentDisplayText] = React.useState<string>('');

    // Calculate the current item to display without side effects
    const getCurrentSyncItemName = React.useMemo(() => {
        let itemToDisplay = '';

        if (displaySyncItems && displaySyncItems.length > 0) {
            // Priority 1: Items that are actively being processed (recordsProcessed >= 0 and status inProgress)
            // This ensures we show the item that's actually being processed
            const itemsBeingProcessed = displaySyncItems.filter(
                (item: AppSyncItem) =>
                    item.status === AppSyncItemStatus.inProgress &&
                    item.recordCount !== undefined &&
                    item.recordCount > 0 &&
                    item.recordsProcessed !== undefined &&
                    item.recordsProcessed >= 0 &&
                    item.recordsProcessed <= item.recordCount
            );

            if (itemsBeingProcessed.length > 0) {
                // Show the item that is actually being processed
                itemToDisplay = itemsBeingProcessed[0]?.displayName || '';
            } else {
                // Priority 2: Items currently in progress (API call in progress, but no recordCount yet)
                const inProgressItems = displaySyncItems.filter(
                    (item: AppSyncItem) => item.status === AppSyncItemStatus.inProgress
                );

                if (inProgressItems.length > 0) {
                    itemToDisplay = inProgressItems[0]?.displayName || '';
                } else {
                    // Priority 3: First queued item (fallback)
                    const queuedItems = displaySyncItems.filter(
                        (item: AppSyncItem) => item.status === AppSyncItemStatus.inQueue
                    );
                    if (queuedItems.length > 0) {
                        itemToDisplay = queuedItems[0]?.displayName || '';
                    }
                }
            }
        }

        return itemToDisplay;
    }, [displaySyncItems]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Update display text state in useEffect to avoid re-render loops
    React.useEffect(() => {
        if (getCurrentSyncItemName && getCurrentSyncItemName !== currentDisplayText) {
            setCurrentDisplayText(getCurrentSyncItemName);
        }
    }, [getCurrentSyncItemName, currentDisplayText]);

    // Reset display text when modal is shown/hidden
    React.useEffect(() => {
        if (!props.show) {
            setCurrentDisplayText('');
        }
    }, [props.show]);

    const getCurrentSyncItem = () => {
        return currentDisplayText || getCurrentSyncItemName;
    };

    const formatElapsedTime = (seconds: number): string => {
        if (seconds < 60) {
            return `${seconds}s`;
        } else {
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = seconds % 60;
            return `${minutes}m ${remainingSeconds}s`;
        }
    };

    const getTotalRecords = () => {
        return displaySyncItems.reduce((total, item) => {
            return total + (item.recordCount || 1); // Default to 1 for APIs that don't report counts
        }, 0);
    };

    const getProcessedRecords = () => {
        return displaySyncItems.reduce((total, item) => {
            if (item.status === AppSyncItemStatus.success) {
                return total + (item.recordCount || 1);
            }
            return total + (item.recordsProcessed || 0);
        }, 0);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const getRecordBasedProgress = React.useMemo(() => {
        const totalRecords = getTotalRecords();
        const processedRecords = getProcessedRecords();

        // Use the persistent flag to determine if we should use record-based progress
        if (!hasRecordCounts) {
            // Fall back to old sync item counting when no record counts have been seen
            const rawProgress = syncProgress.currentIndex;
            const currentProgress = rawProgress;
            const finalProgress = Math.max(currentProgress, maxProgressReached);

            return finalProgress;
        }

        // Calculate record-based progress with 10% minimum
        const rawProgress = totalRecords > 0 ? (processedRecords / totalRecords) * 100 : 0;
        const currentProgress = Math.max(rawProgress, 10); // Minimum 10% progress

        // Check if total records changed (API updated the count) vs processing progress
        const totalRecordsChanged = totalRecords !== lastTotalRecords;

        if (totalRecordsChanged) {
            // Total count updated - allow ping-ponging
            return currentProgress;
        } else {
            // Processing progress - prevent backward movement (monotonic)
            const adjustedProgress = Math.max(currentProgress, maxProgressReached);
            return adjustedProgress;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displaySyncItems, syncProgress, hasRecordCounts, maxProgressReached, lastTotalRecords]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Handle state updates in separate effects to avoid re-render loops
    React.useEffect(() => {
        const totalRecords = getTotalRecords();
        const processedRecords = getProcessedRecords();

        if (!hasRecordCounts) {
            const rawProgress = syncProgress.currentIndex;
            const currentProgress = Math.max(rawProgress, 10);
            if (currentProgress > maxProgressReached) {
                setMaxProgressReached(currentProgress);
            }
            setShouldAnimateProgress(true);
            setLastProgress(Math.max(currentProgress, maxProgressReached));
            return;
        }

        const rawProgress = totalRecords > 0 ? (processedRecords / totalRecords) * 100 : 0;
        const currentProgress = Math.max(rawProgress, 10);
        const totalRecordsChanged = totalRecords !== lastTotalRecords;

        if (totalRecordsChanged) {
            setLastTotalRecords(totalRecords);
            setMaxProgressReached(currentProgress);

            const isMovingBackward = currentProgress < lastProgress;
            setShouldAnimateProgress(!isMovingBackward);
            setLastProgress(currentProgress);

            if (isMovingBackward) {
                setTimeout(() => setShouldAnimateProgress(true), 100);
            }
        } else {
            const adjustedProgress = Math.max(currentProgress, maxProgressReached);
            if (adjustedProgress > maxProgressReached) {
                setMaxProgressReached(adjustedProgress);
            }
            setShouldAnimateProgress(true);
            setLastProgress(adjustedProgress);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displaySyncItems, syncProgress, hasRecordCounts]);

    const getDisplayMessage = () => {
        // Always show "Loading app data" - removed processing/loading distinction
        return props.message || 'LOADING...';
    };

    const logoutUser = () => {
        dispatch(userLogoutSessionOnly());
    };

    return (
        <Modal show={props.show} className="loading-modal" onHide={() => null}>
            <Modal.Body className="text-center">
                <img style={{ width: '275.84px' }} src={logo} alt="loading" />
                <h3 className="modal-title">{getDisplayMessage()}</h3>

                {displaySyncItems && displaySyncItems.length > 0 && (
                    <div className="loading-container">
                        <ProgressBar
                            className={`loading-progress ${!shouldAnimateProgress ? 'no-animation' : ''}`}
                            now={getRecordBasedProgress}
                            max={100}
                        />
                        <div>
                            {`Loading ${getCurrentSyncItem()}`}
                            {props.show && <span>... {formatElapsedTime(elapsedTime)}</span>}
                        </div>
                        {props.downloadSpeed ? (
                            <div className="loading-speed">
                                {t('common:loadingDownloadSpeed', { speed: props.downloadSpeed.toFixed(2) })}
                            </div>
                        ) : null}
                        <div className="loading-action-container">
                            <button className="exit-logOut-btn" onClick={logoutUser}>
                                {t('common:exitAndLogOut')}
                            </button>
                            <button className="retry-btn" onClick={props.restartAppSync}>
                                {t('common:retry')}
                            </button>
                        </div>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default Loading;
